import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message
} from 'antd';
import {
  EyeOutlined,
  GlobalOutlined,
  GoogleOutlined,
  LineChartOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { analyticsAPI } from '../api/analytics';

const { Text } = Typography;

function formatNumber(value, maximumFractionDigits = 0) {
  return new Intl.NumberFormat('fa-IR', { maximumFractionDigits }).format(Number(value) || 0);
}

function formatPercent(value) {
  return `${formatNumber((Number(value) || 0) * 100, 1)}٪`;
}

function formatDuration(seconds) {
  const value = Number(seconds) || 0;
  if (value < 60) return `${formatNumber(value, 0)} ثانیه`;
  return `${formatNumber(value / 60, 1)} دقیقه`;
}

function metricColumn(title, dataIndex, options = {}) {
  return {
    title,
    dataIndex,
    align: 'right',
    render: (value) => options.percent
      ? formatPercent(value)
      : formatNumber(value, options.decimals || 0)
  };
}

function setupWarnings(setup) {
  const warnings = [];
  if (!setup?.hasCredentials) {
    warnings.push('کلید سرویس گوگل تنظیم نشده است.');
  }
  if (setup?.credentialsError) {
    warnings.push(setup.credentialsError);
  }
  if (!setup?.ga4PropertyId) {
    warnings.push('شناسه GA4 Property تنظیم نشده است.');
  }
  if (!setup?.searchConsoleSiteUrl) {
    warnings.push('آدرس property سرچ کنسول تنظیم نشده است.');
  }
  return warnings;
}

function ErrorAlert({ message: errorMessage }) {
  if (!errorMessage) return null;
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 16 }}
      message="دریافت داده ناموفق بود"
      description={errorMessage}
    />
  );
}

function SourceStatus({ setup }) {
  return (
    <Space wrap style={{ marginBottom: 16 }}>
      <Tag color={setup?.analyticsEnabled ? 'green' : 'default'} icon={<LineChartOutlined />}>
        GA4: {setup?.analyticsEnabled ? setup.ga4PropertyId : 'تنظیم نشده'}
      </Tag>
      <Tag color={setup?.searchConsoleEnabled ? 'green' : 'default'} icon={<SearchOutlined />}>
        Search Console: {setup?.searchConsoleEnabled ? setup.searchConsoleSiteUrl : 'تنظیم نشده'}
      </Tag>
      {setup?.serviceAccountEmail && (
        <Tag color="blue" icon={<GoogleOutlined />}>{setup.serviceAccountEmail}</Tag>
      )}
    </Space>
  );
}

function AnalyticsTables({ analytics }) {
  if (!analytics?.enabled || analytics.error) return null;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card title="روند روزانه GA4" size="small" bordered={false}>
          <Table
            rowKey="date"
            size="small"
            dataSource={analytics.timeline || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'روز', dataIndex: 'date' },
              metricColumn('کاربر فعال', 'activeUsers'),
              metricColumn('نشست', 'sessions'),
              metricColumn('بازدید صفحه', 'screenPageViews')
            ]}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="صفحات پربازدید GA4" size="small" bordered={false}>
          <Table
            rowKey="pagePath"
            size="small"
            dataSource={analytics.topPages || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'مسیر', dataIndex: 'pagePath', ellipsis: true },
              metricColumn('بازدید صفحه', 'screenPageViews'),
              metricColumn('کاربر فعال', 'activeUsers'),
              metricColumn('نشست', 'sessions')
            ]}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="کانال‌های ورودی GA4" size="small" bordered={false}>
          <Table
            rowKey="sessionDefaultChannelGroup"
            size="small"
            dataSource={analytics.channels || []}
            pagination={false}
            columns={[
              { title: 'کانال', dataIndex: 'sessionDefaultChannelGroup' },
              metricColumn('نشست', 'sessions'),
              metricColumn('کاربر فعال', 'activeUsers')
            ]}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="کشورها در GA4" size="small" bordered={false}>
          <Table
            rowKey="country"
            size="small"
            dataSource={analytics.countries || []}
            pagination={false}
            columns={[
              { title: 'کشور', dataIndex: 'country' },
              metricColumn('کاربر فعال', 'activeUsers'),
              metricColumn('نشست', 'sessions')
            ]}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
    </Row>
  );
}

function SearchConsoleTables({ searchConsole }) {
  if (!searchConsole?.enabled || searchConsole.error) return null;

  const searchColumns = (dimension, label) => [
    { title: label, dataIndex: dimension, ellipsis: true },
    metricColumn('کلیک', 'clicks'),
    metricColumn('نمایش', 'impressions'),
    metricColumn('CTR', 'ctr', { percent: true }),
    metricColumn('جایگاه', 'position', { decimals: 1 })
  ];

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card title="کوئری‌های سرچ کنسول" size="small" bordered={false}>
          <Table
            rowKey="query"
            size="small"
            dataSource={searchConsole.queries || []}
            pagination={{ pageSize: 10 }}
            columns={searchColumns('query', 'عبارت جستجو')}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="صفحات در سرچ کنسول" size="small" bordered={false}>
          <Table
            rowKey="page"
            size="small"
            dataSource={searchConsole.pages || []}
            pagination={{ pageSize: 10 }}
            columns={searchColumns('page', 'صفحه')}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="کشورها در سرچ کنسول" size="small" bordered={false}>
          <Table
            rowKey="country"
            size="small"
            dataSource={searchConsole.countries || []}
            pagination={false}
            columns={searchColumns('country', 'کشور')}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title="دستگاه‌ها در سرچ کنسول" size="small" bordered={false}>
          <Table
            rowKey="device"
            size="small"
            dataSource={searchConsole.devices || []}
            pagination={false}
            columns={searchColumns('device', 'دستگاه')}
            locale={{ emptyText: <Empty description="داده‌ای وجود ندارد" /> }}
          />
        </Card>
      </Col>
    </Row>
  );
}

export default function GoogleInsightsPanel() {
  const [days, setDays] = useState(28);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const loadReport = async (selectedDays = days) => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getGoogleInsights({ days: selectedDays });
      if (res.data.success) {
        setReport(res.data);
      } else {
        message.error(res.data.message || 'خطا در دریافت گزارش گوگل');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در اتصال به سرویس گوگل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const warnings = useMemo(() => (report ? setupWarnings(report.setup) : []), [report]);
  const analyticsSummary = report?.analytics?.summary || {};
  const searchSummary = report?.searchConsole?.summary || {};

  return (
    <Spin spinning={loading}>
      <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Segmented
            value={days}
            onChange={(value) => {
              setDays(value);
              loadReport(value);
            }}
            options={[
              { label: '۷ روز', value: 7 },
              { label: '۲۸ روز', value: 28 },
              { label: '۹۰ روز', value: 90 }
            ]}
          />
          {report?.range && (
            <Text type="secondary">
              {report.range.startDate} تا {report.range.endDate}
            </Text>
          )}
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => loadReport()} loading={loading}>
          بروزرسانی
        </Button>
      </Space>

      <SourceStatus setup={report?.setup} />

      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="اتصال گوگل کامل نیست"
          description={(
            <div>
              <div>{warnings.join(' ')}</div>
              <div style={{ marginTop: 8 }}>
                <Text code>GOOGLE_SERVICE_ACCOUNT_JSON</Text>{' '}
                <Text code>GOOGLE_GA4_PROPERTY_ID</Text>{' '}
                <Text code>GOOGLE_SEARCH_CONSOLE_SITE_URL</Text>
              </div>
            </div>
          )}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="کاربران فعال GA4"
              value={analyticsSummary.activeUsers || 0}
              formatter={formatNumber}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="بازدید صفحات GA4"
              value={analyticsSummary.screenPageViews || 0}
              formatter={formatNumber}
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="کلیک سرچ"
              value={searchSummary.clicks || 0}
              formatter={formatNumber}
              prefix={<SearchOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="نمایش سرچ"
              value={searchSummary.impressions || 0}
              formatter={formatNumber}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'ga4',
            label: 'Google Analytics 4',
            children: (
              <>
                <ErrorAlert message={report?.analytics?.error} />
                {report?.analytics?.enabled && !report.analytics.error && (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card size="small" bordered={false}>
                          <Statistic title="نشست‌ها" value={analyticsSummary.sessions || 0} formatter={formatNumber} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card size="small" bordered={false}>
                          <Statistic title="کاربران جدید" value={analyticsSummary.newUsers || 0} formatter={formatNumber} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card size="small" bordered={false}>
                          <Statistic title="نرخ تعامل" value={formatPercent(analyticsSummary.engagementRate)} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card size="small" bordered={false}>
                          <Statistic title="میانگین نشست" value={formatDuration(analyticsSummary.averageSessionDuration)} />
                        </Card>
                      </Col>
                    </Row>
                    <AnalyticsTables analytics={report.analytics} />
                  </>
                )}
                {!report?.analytics?.enabled && <Empty description="GA4 هنوز تنظیم نشده است" />}
              </>
            )
          },
          {
            key: 'search-console',
            label: 'Search Console',
            children: (
              <>
                <ErrorAlert message={report?.searchConsole?.error} />
                {report?.searchConsole?.enabled && !report.searchConsole.error && (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={12} lg={8}>
                        <Card size="small" bordered={false}>
                          <Statistic title="CTR" value={formatPercent(searchSummary.ctr)} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={8}>
                        <Card size="small" bordered={false}>
                          <Statistic title="میانگین جایگاه" value={formatNumber(searchSummary.position, 1)} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={8}>
                        <Card size="small" bordered={false}>
                          <Statistic title="کلیک / نمایش" value={`${formatNumber(searchSummary.clicks)} / ${formatNumber(searchSummary.impressions)}`} />
                        </Card>
                      </Col>
                    </Row>
                    <SearchConsoleTables searchConsole={report.searchConsole} />
                  </>
                )}
                {!report?.searchConsole?.enabled && <Empty description="Search Console هنوز تنظیم نشده است" />}
              </>
            )
          }
        ]}
      />
    </Spin>
  );
}
